import React, {useEffect, useState} from "react";
import {
    Card,
    CardContent,
    Checkbox,
    createStyles,
    FormControlLabel,
    makeStyles,
    TableFooter,
    TablePagination,
    Theme,
    Typography,
    useMediaQuery,
    useTheme,
    withStyles
} from "@material-ui/core";
import AppColors from "../colors/AppColors";
import {grey} from "@material-ui/core/colors";
import {faFire} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import Game from "../models/Game";
import {Constants} from "../constants/Constants";
import axios from "axios";
import EndPoints from "../net/EndPoints";
import RequestUtils from "../utils/RequestUtils";
import SockJS from "sockjs-client";
import * as stomp from "@stomp/stompjs";
import UsersManager from "../managers/UsersManager";
import {useDispatch} from "react-redux";
import SimpleTableRow from "../views/SimpleTableRow";
import TablePaginationActions from "../views/TablePaginationActions";
import GenUtils from "../utils/GenUtils";

// noinspection JSUnusedLocalSymbols
const useStyles = makeStyles((theme) => ({
    checkbox: {
        color: AppColors.GREY_TEXT,
        '&.Mui-checked': {
            color: AppColors.ACCENT_COLOR
        }
    },
    checkBoxLabel: {
        fontSize: 15,
        color: AppColors.GREY_TEXT
    },
    select: {
        color: grey[400]
    }
}));

// noinspection JSUnusedLocalSymbols
export const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
        head: {
            backgroundColor: AppColors.CARD_SECTION_HEADER_COLORS,
            color: grey[400],
            fontWeight: 600,
            borderBottomLeftRadius: 0,
            borderTopRightRadius: 5,
            borderBottomRightRadius: 0,
            borderTopLeftRadius: 5,
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 5,
            paddingRight: 5,
            border: `1px solid ${AppColors.lightenOrDarken(AppColors.PRIMARY_COLOR, 20)}`
        },
        body: {
            fontSize: 15,
            paddingTop: 20,
            paddingBottom: 20,
            fontWeight: 600,
            color: AppColors.EASE_GRAY,
            paddingRight: 10,
            border: 'none',
        },
    }),
)(TableCell);

export default function LiveStats(props: any) {

    const classes = useStyles();
    const theme = useTheme();
    const canGoRow = useMediaQuery(theme.breakpoints.up(1050));
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const gameType = props.gameType ?? 0;
    const [showOnlyMyStats, setShowOnlyMyStats] = useState([false]);
    const [loaded] = useState(false);
    const [games, setGames] = useState<Game[]>([]);
    const emptyRows = GenUtils.computeEmptyRows(rowsPerPage, games, page);

    const dispatch = useDispatch();

    let stompClient: any;

    const processIncomingGame = (frame: any) => {
        let game = JSON.parse(frame.body);
        if (game.id.length > 1) {
            if (showOnlyMyStats[0]) {
                let loggedInUser = JSON.parse(UsersManager.getLoggedInUser() as string)
                if (loggedInUser !== undefined) {
                    if (loggedInUser["previousServerSeed"] === game.previousServerSeed) {
                        setGames(games => [game, ...games]);
                    }
                }
            } else {
                if (game.gameType === gameType) {
                    if (games.length > 200) {
                        games.splice(games.length - 1, 1)
                        setGames([game, ...games])
                    } else {
                        setGames(games => [game, ...games]);
                    }
                }
            }
        }
    }

    const processNewIncomingDataForLoggedInUser = (frame: any, loggedInUser: any) => {
        let userData = JSON.parse(frame.body);
        if (userData["id"] === loggedInUser["id"]) {
            UsersManager.saveLoggedInUser(JSON.stringify(userData));
            dispatch({
                type: Constants.LOGGED_IN_USER_DATA_CHANGED,
                payload: JSON.stringify(userData)
            });
        }
    }

    const initWebSocket = () => {
        const sock = new SockJS(EndPoints.BASE_URL + "/live-data");
        stompClient = stomp.Stomp.over(sock);
        stompClient.reconnect_delay = 1000
        stompClient.debug = () => {
        };
        stompClient.connect({}, () => {
            stompClient.subscribe("/topic/games", (frame: any) => {
                processIncomingGame(frame);
            });
            let loggedInUser = JSON.parse(UsersManager.getLoggedInUser() as string)
            if (loggedInUser !== undefined) {
                stompClient.subscribe("/topic/" + loggedInUser["id"], (frame: any) => {
                    processNewIncomingDataForLoggedInUser(frame, loggedInUser);
                });
            }
        });
    }

    const disconnectWSClient = () => {
        if (stompClient !== null) {
            stompClient.disconnect();
        }
    }

    useEffect(() => {
        initWebSocket();
        return () => {
            disconnectWSClient();
        }
    }, [loaded]);

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const handleStatsCheckBoxChange = (event: any) => {
        setShowOnlyMyStats((showMyStats) => {
            showMyStats[0] = event.target.checked;
            return [...showMyStats]
        });
    }

    const fetchStats = () => {
        axios.get(showOnlyMyStats[0] ? EndPoints.getMyGamesRoute(gameType)
            : EndPoints.getGames(gameType), {
            headers: RequestUtils.getHeaders()
        }).then(response => {
            let retrievedGames = response["data"]["data"];
            setGames(retrievedGames);
        }).catch(_error => {
        });
    }

    useEffect(() => {
        fetchStats();
    }, [gameType, showOnlyMyStats]);

    return (
        <Card
            style={{
                width: '98%',
                marginLeft: 10,
                marginRight: 10,
                marginTop: 10,
                backgroundColor: AppColors.CARD_BACKGROUND_COLOR
            }}>
            <CardContent>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <FontAwesomeIcon icon={faFire} style={{
                            color: 'white',
                        }}/>
                        <Typography style={{marginLeft: 10}}
                                    dangerouslySetInnerHTML={{__html: `<span style='color:${grey[200]};'><b>Live Stats</b></span>`}}/>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value={showOnlyMyStats[0]}
                                    checked={showOnlyMyStats[0]}
                                    onChange={handleStatsCheckBoxChange}
                                    name="statsToggle"
                                    className={classes.checkbox}
                                    icon={<CheckBoxOutlineBlankIcon fontSize="small"/>}
                                    checkedIcon={<CheckBoxIcon fontSize="small"/>}
                                />
                            }
                            classes={{
                                label: classes.checkBoxLabel
                            }}
                            label="My Stats"
                            style={{
                                marginLeft: canGoRow ? 20 : 0
                            }}
                        />
                    </div>
                    <TableContainer>
                        <Table aria-label="stats-table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">ID</StyledTableCell>
                                    <StyledTableCell align="center">USERNAME</StyledTableCell>
                                    <StyledTableCell align="center">GAME</StyledTableCell>
                                    {
                                        gameType == 1 &&
                                        <StyledTableCell align="center">TYPE</StyledTableCell>
                                    }
                                    {
                                        gameType == 1 &&
                                        <StyledTableCell align="center">STAKE (BTC)</StyledTableCell>
                                    }
                                    {
                                        gameType == 0 &&
                                        <StyledTableCell align="center">ROLL</StyledTableCell>
                                    }
                                    {
                                        gameType == 1 &&
                                        <StyledTableCell align="center">PREDICTIONS</StyledTableCell>
                                    }
                                    {
                                        gameType == 1 &&
                                        <StyledTableCell align="center">WINNING NOS</StyledTableCell>
                                    }
                                    <StyledTableCell align="center">WON (BTC)</StyledTableCell>
                                    {
                                        gameType === 1 &&
                                        <StyledTableCell align="center">LOST (BTC)</StyledTableCell>
                                    }
                                    <StyledTableCell align="center">WHEN</StyledTableCell>
                                    <StyledTableCell align="center">VERIFY</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    (rowsPerPage > 0 ? games.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : games
                                    ).map((row: Game, index: number) => (
                                        <SimpleTableRow row={row} key={row.id} position={index}/>
                                    ))}
                                {
                                    emptyRows > 0 && (
                                        <TableRow style={{height: 53 * emptyRows}}>
                                            <TableCell colSpan={6}/>
                                        </TableRow>
                                    )
                                }
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        style={{
                                            border: 'none',
                                            color: grey[400],
                                        }}
                                        classes={{
                                            selectIcon: classes.select
                                        }}
                                        rowsPerPageOptions={[10, 20, 30, {label: 'All', value: -1}]}
                                        colSpan={6}
                                        count={games.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        SelectProps={{
                                            inputProps: {'aria-label': 'rows per page'},
                                            native: true,
                                        }}
                                        onChangePage={handleChangePage}
                                        onChangeRowsPerPage={handleChangeRowsPerPage}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </div>
            </CardContent>
        </Card>
    );
}